import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiChevronDown, FiChevronRight, FiEdit2, FiSearch } from 'react-icons/fi';

import HeaderCard from '../../components/Header/HeaderCard';
import { apiService } from '../../components/Config/api.service';
import { Modal } from '../../components/Utils/ModalShow';
import { notify } from '../../components/Utils/Notifications';
import AffectationOrganigramme from '../Employee/AffectationOrganigramme';

/**
 * L'organigramme de la société, côté RH (Yros/Kavero#494).
 *
 * Le serveur rend une ligne par salarié et son responsable quand il tient ;
 * l'écran assemble l'arbre. Il se déplie nœud par nœud : à plusieurs
 * centaines de salariés, tout montrer d'un coup ne se lit plus. La recherche
 * et le filtre « ce qui manque » ne gardent que les personnes trouvées et le
 * chemin qui y mène, déplié.
 *
 * Les anomalies sont celles de l'indicateur C30, posées sur la personne. Le
 * seul moyen d'écrire est l'affectation existante (C29), ouverte sur un
 * salarié : pas de glisser-déposer, qui réaffecterait une équipe par erreur.
 */

const RACINE = 'racine';

const ANOMALIES = {
  sans_responsable: 'bg-amber-50 text-amber-700 border-amber-200',
  responsable_archive: 'bg-red-50 text-red-700 border-red-200',
  responsable_autre_societe: 'bg-red-50 text-red-700 border-red-200',
  boucle: 'bg-red-50 text-red-700 border-red-200',
  ecart_conges: 'bg-orange-50 text-orange-700 border-orange-200',
};

const sansAccent = (texte) => (texte || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const initiales = (nom) => nom.split(/\s+/).filter(Boolean).slice(0, 2)
  .map((mot) => mot[0].toUpperCase()).join('');

/** Les enfants de chaque responsable, triés par nom ; `RACINE` pour les autres. */
export function construireArbre(salaries) {
  const enfants = { [RACINE]: [] };
  salaries.forEach((s) => { enfants[s.id] = []; });
  salaries.forEach((s) => {
    const parent = s.parent_id !== null && enfants[s.parent_id] ? s.parent_id : RACINE;
    enfants[parent].push(s);
  });
  Object.values(enfants).forEach((liste) => liste.sort((a, b) => a.nom.localeCompare(b.nom, 'fr')));
  return enfants;
}

/**
 * Ceux qu'un filtre garde, et leurs ancêtres : le chemin jusqu'à eux reste
 * visible et se déplie.
 */
export function cheminsVers(salaries, garde) {
  const parId = Object.fromEntries(salaries.map((s) => [s.id, s]));
  const visibles = new Set();
  const deplies = new Set();
  salaries.filter(garde).forEach((s) => {
    visibles.add(s.id);
    let parent = s.parent_id;
    while (parent !== null && parId[parent] && !deplies.has(parent)) {
      visibles.add(parent);
      deplies.add(parent);
      parent = parId[parent].parent_id;
    }
  });
  return { visibles, deplies };
}

function Noeud({ salarie, enfants, niveau, deplies, visibles, trouves, onBasculer, onOuvrir, onChanger }) {
  const { t } = useTranslation();
  const sesEnfants = (enfants[salarie.id] || []).filter((e) => !visibles || visibles.has(e.id));
  const nbDirects = (enfants[salarie.id] || []).length;
  const ouvert = deplies.has(salarie.id);
  const sousTitre = [salarie.poste, salarie.departement].filter(Boolean).join(' · ');

  return (
    <li data-testid={`noeud-${salarie.id}`}>
      <div
        className={`group flex items-center gap-2 rounded-lg py-1.5 pr-2 hover:bg-gray-50 ${
          trouves?.has(salarie.id) ? 'bg-yellow-50' : ''}`}
        style={{ paddingLeft: `${niveau * 20}px` }}
      >
        {nbDirects > 0 ? (
          <button type="button" onClick={() => onBasculer(salarie.id)} aria-expanded={ouvert}
            aria-label={t(ouvert ? 'organigrammeArbre.replier' : 'organigrammeArbre.deplier', { nom: salarie.nom })}
            data-testid={`basculer-${salarie.id}`}
            className="p-1 rounded text-gray-500 hover:bg-gray-200">
            {ouvert ? <FiChevronDown aria-hidden="true" /> : <FiChevronRight aria-hidden="true" />}
          </button>
        ) : <span className="w-6 shrink-0" aria-hidden="true" />}
        <span aria-hidden="true"
          className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center bg-[--color-primary] text-white text-xs font-semibold">
          {initiales(salarie.nom)}
        </span>
        {/* Les anomalies sous le poste : sur téléphone, à côté du nom, elles le coupaient. */}
        <div className="min-w-0">
          <button type="button" onClick={() => onOuvrir(salarie.id)} className="block max-w-full text-left">
            <span className="block text-sm font-medium text-gray-800 truncate hover:text-[--color-primary]">{salarie.nom}</span>
            {sousTitre && <span className="block text-xs text-gray-500 truncate">{sousTitre}</span>}
          </button>
          {salarie.anomalies.length > 0 && (
            <span className="mt-0.5 flex flex-wrap gap-1">
              {salarie.anomalies.map((anomalie) => (
                <span key={anomalie} data-testid={`anomalie-${salarie.id}-${anomalie}`}
                  className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${ANOMALIES[anomalie] || ANOMALIES.boucle}`}>
                  {t(`organigrammeArbre.anomalies.${anomalie}`)}
                </span>
              ))}
            </span>
          )}
        </div>
        {nbDirects > 0 && (
          <span className="shrink-0 text-xs text-gray-500" title={t('organigrammeArbre.directs', { count: nbDirects })}>
            {t('organigrammeArbre.directsCourt', { count: nbDirects })}
          </span>
        )}
        <button type="button" onClick={() => onChanger(salarie)} data-testid={`changer-${salarie.id}`}
          title={t('organigrammeArbre.changerResponsable')} aria-label={t('organigrammeArbre.changerResponsable')}
          className="ml-auto shrink-0 p-1.5 rounded text-gray-400 hover:text-[--color-primary] hover:bg-gray-100 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100">
          <FiEdit2 aria-hidden="true" />
        </button>
      </div>
      {ouvert && sesEnfants.length > 0 && (
        <ul>
          {sesEnfants.map((enfant) => (
            <Noeud key={enfant.id} salarie={enfant} enfants={enfants} niveau={niveau + 1} deplies={deplies}
              visibles={visibles} trouves={trouves} onBasculer={onBasculer} onOuvrir={onOuvrir} onChanger={onChanger} />
          ))}
        </ul>
      )}
    </li>
  );
}

function Organigramme() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [salaries, setSalaries] = useState(null);
  const [erreur, setErreur] = useState(false);
  const [deplies, setDeplies] = useState(new Set());
  const [recherche, setRecherche] = useState('');
  const [manques, setManques] = useState(false);
  const [aChanger, setAChanger] = useState(null);
  const [departements, setDepartements] = useState([]);

  const charger = useCallback(() => {
    setErreur(false);
    return apiService.lireArbreOrganigramme()
      .then((reponse) => {
        if (reponse?.ok) setSalaries(reponse.salaries || []);
        else setErreur(true);
      })
      .catch(() => setErreur(true));
  }, []);

  useEffect(() => { charger(); }, [charger]);

  useEffect(() => {
    let actif = true;
    Promise.resolve()
      .then(() => apiService.lireOrganigrammeDepartements())
      .then((reponse) => {
        if (actif && reponse?.ok) {
          setDepartements(reponse.departements.map((d) => ({ value: d.id, label: d.nom })));
        }
      })
      .catch(() => {});
    return () => { actif = false; };
  }, []);

  const enfants = useMemo(() => construireArbre(salaries || []), [salaries]);
  const nbManques = useMemo(() => (salaries || []).filter((s) => s.anomalies.length > 0).length, [salaries]);

  // Recherche ou filtre : seulement les personnes gardées et leur chemin, déplié.
  const filtre = useMemo(() => {
    const cherche = sansAccent(recherche.trim());
    if (!salaries || (!cherche && !manques)) return null;
    const garde = (s) => (!cherche || sansAccent(s.nom).includes(cherche))
      && (!manques || s.anomalies.length > 0);
    const { visibles, deplies: chemins } = cheminsVers(salaries, garde);
    return { visibles, chemins, trouves: new Set(salaries.filter(garde).map((s) => s.id)) };
  }, [salaries, recherche, manques]);

  const basculer = (id) => setDeplies((avant) => {
    const apres = new Set(avant);
    if (apres.has(id)) apres.delete(id); else apres.add(id);
    return apres;
  });
  const toutDeplier = () => setDeplies(new Set(Object.keys(enfants)
    .filter((id) => id !== RACINE && enfants[id].length > 0).map(Number)));

  // Avec un filtre, ses chemins se déplient en plus de ce que la RH a ouvert.
  const depliesVus = filtre ? new Set([...deplies, ...filtre.chemins]) : deplies;
  const racines = enfants[RACINE].filter((s) => !filtre || filtre.visibles.has(s.id));
  // À la racine, les responsables d'abord : les salariés seuls, sans équipe ni
  // responsable, sont souvent les plus nombreux et noieraient l'arbre.
  const responsables = racines.filter((s) => enfants[s.id].length > 0);
  const isoles = racines.filter((s) => enfants[s.id].length === 0);

  const props = {
    enfants, niveau: 0, deplies: depliesVus, visibles: filtre?.visibles, trouves: filtre?.trouves,
    onBasculer: basculer, onOuvrir: (id) => navigate(`/employees/${id}?tab=equipe`), onChanger: setAChanger,
  };

  return (
    <section className="space-y-4">
      <HeaderCard title={t('organigrammeArbre.titre')} text={t('organigrammeArbre.description')} />

      <div className="bg-white rounded-xl border border-gray-100 p-4 flex flex-wrap items-center gap-3">
        <label className="relative grow basis-full sm:basis-64">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
          <input type="search" value={recherche} onChange={(e) => setRecherche(e.target.value)}
            data-testid="organigramme-recherche" placeholder={t('organigrammeArbre.rechercher')}
            aria-label={t('organigrammeArbre.rechercher')}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[--color-primary]" />
        </label>
        {nbManques > 0 && (
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={manques} onChange={(e) => setManques(e.target.checked)}
              data-testid="organigramme-manques" className="accent-[--color-primary]" />
            {t('organigrammeArbre.seulementManques', { count: nbManques })}
          </label>
        )}
        <div className="flex gap-2 sm:ml-auto">
          <button type="button" onClick={toutDeplier} data-testid="organigramme-tout-deplier"
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50">
            {t('organigrammeArbre.toutDeplier')}
          </button>
          <button type="button" onClick={() => setDeplies(new Set())}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50">
            {t('organigrammeArbre.toutReplier')}
          </button>
        </div>
      </div>

      {erreur && (
        <p role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {t('organigrammeArbre.erreur')}
        </p>
      )}
      {!erreur && salaries === null && <p className="text-sm text-gray-500">{t('organigrammeArbre.chargement')}</p>}
      {salaries && racines.length === 0 && (
        <p data-testid="organigramme-vide" className="rounded-xl bg-white border border-gray-100 p-4 text-sm text-gray-500">
          {t(filtre ? 'organigrammeArbre.aucunResultat' : 'organigrammeArbre.vide')}
        </p>
      )}

      {responsables.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-3">
          <ul data-testid="organigramme-arbre">
            {responsables.map((s) => <Noeud key={s.id} salarie={s} {...props} />)}
          </ul>
        </div>
      )}
      {isoles.length > 0 && (
        <details data-testid="organigramme-isoles" open={Boolean(filtre)}
          className="bg-white rounded-xl border border-gray-100 p-3">
          <summary className="cursor-pointer text-sm font-medium text-gray-700">
            {t('organigrammeArbre.isoles', { count: isoles.length })}
          </summary>
          <ul className="mt-2">
            {isoles.map((s) => <Noeud key={s.id} salarie={s} {...props} />)}
          </ul>
        </details>
      )}

      <Modal isOpen={aChanger !== null} onClose={() => setAChanger(null)}
        title={t('organigrammeArbre.changerResponsableDe', { nom: aChanger?.nom })} size={500}>
        {aChanger && (
          <AffectationOrganigramme
            employeIds={[aChanger.id]}
            departements={departements}
            remplacerParDefaut
            onAnnuler={() => setAChanger(null)}
            onTermine={(modifies) => {
              notify(t('organigrammeGroupe.termine', { n: modifies }), '', 2);
              setAChanger(null);
              charger();
            }}
          />
        )}
      </Modal>
    </section>
  );
}

export default Organigramme;
